export type ProductCategory = "letter-set" | "accessory";
export type LetterStyle = "Modern" | "ADM" | "Condensed";
export type LetterColor = "Black" | "Red" | "White";

export interface LetterSize {
  label: string; // e.g. "4″ on 4-7/8″"
  letterHeight: string; // e.g. "4″"
  panelHeight: string; // e.g. "4-7/8″"
  price: number; // cents, list price + 10%
}

export interface Product {
  id: string;
  name: string;
  slug: string;
  category: ProductCategory;
  description: string;
  price: number; // cents
  image: string;
  features?: string[];
  // Letter set specific
  style?: LetterStyle;
  sizes?: LetterSize[];
  colors?: LetterColor[];
  // Accessory specific
  variants?: { label: string; price: number }[];
  variantImages?: Record<string, string>; // maps variant label keywords to image
}

const MODERN_SIZES: LetterSize[] = [
  { label: '3″ on 3-7/8″', letterHeight: '3″', panelHeight: '3-7/8″', price: 15070 },
  { label: '4″ on 4-7/8″', letterHeight: '4″', panelHeight: '4-7/8″', price: 16610 },
  { label: '6″ on 6-7/8″', letterHeight: '6″', panelHeight: '6-7/8″', price: 23430 },
  { label: '8″ on 8-7/8″', letterHeight: '8″', panelHeight: '8-7/8″', price: 38720 },
  { label: '8″ on 9-7/8″', letterHeight: '8″', panelHeight: '9-7/8″', price: 37070 },
  { label: '10″ on 11-7/8″', letterHeight: '10″', panelHeight: '11-7/8″', price: 49390 },
];

const STANDARD_SIZES: LetterSize[] = [
  { label: '4″ on 4-7/8″', letterHeight: '4″', panelHeight: '4-7/8″', price: 16610 },
  { label: '6″ on 6-7/8″', letterHeight: '6″', panelHeight: '6-7/8″', price: 23430 },
  { label: '8″ on 8-7/8″', letterHeight: '8″', panelHeight: '8-7/8″', price: 38720 },
  { label: '8″ on 9-7/8″', letterHeight: '8″', panelHeight: '9-7/8″', price: 37070 },
  { label: '10″ on 11-7/8″', letterHeight: '10″', panelHeight: '11-7/8″', price: 49390 },
];

import productModern from "@/assets/product-modern.png";
import productAdm from "@/assets/product-adm.png";
import productCondensed from "@/assets/product-condensed.png";
import productChangeArm from "@/assets/product-change-arm.png";
import productStorageCase from "@/assets/product-storage-case.png";
import productStorageCase2 from "@/assets/product-storage-case-2.png";
import productStorageCabinet from "@/assets/product-storage-cabinet.png";
import productProntoTrack from "@/assets/product-pronto-track.jpg";
import productTrackRivets from "@/assets/product-track-rivets.jpg";
import fontModern from "@/assets/font-modern.png";
import fontAdm from "@/assets/font-adm.png";

export { fontModern, fontAdm };

export const products: Product[] = [
  // Letter Sets
  {
    id: "modern-letter-set",
    name: "Modern Letter Set",
    slug: "modern-letter-set",
    category: "letter-set",
    style: "Modern",
    description: "Clean, contemporary letterforms with excellent readability. Injection-molded from durable polycarbonate with UV-resistant eco-friendly inks. Set of 100 characters.",
    price: 0,
    image: productModern,
    sizes: MODERN_SIZES,
    colors: ["Black", "Red", "White"],
    features: [
      "Set of 100 characters",
      "Injection-molded polycarbonate",
      "UV-resistant eco-friendly inks",
      "Racks into Pronto Track instantly",
      "No tools required for changes",
    ],
  },
  {
    id: "adm-letter-set",
    name: "ADM Letter Set",
    slug: "adm-letter-set",
    category: "letter-set",
    style: "ADM",
    description: "Bold, attention-grabbing letterforms ideal for high-visibility signage. Durable injection-molded polycarbonate with UV-resistant eco-friendly inks. Set of 100 characters.",
    price: 0,
    image: productAdm,
    sizes: STANDARD_SIZES,
    colors: ["Black", "Red", "White"],
    features: [
      "Set of 100 characters",
      "Injection-molded polycarbonate",
      "UV-resistant eco-friendly inks",
      "Racks into Pronto Track instantly",
      "No tools required for changes",
    ],
  },
  {
    id: "condensed-letter-set",
    name: "Condensed Letter Set",
    slug: "condensed-letter-set",
    category: "letter-set",
    style: "Condensed",
    description: "Narrow letterforms that fit more text per line — perfect when space is tight. Durable injection-molded polycarbonate with UV-resistant eco-friendly inks. Set of 100 characters.",
    price: 0,
    image: productCondensed,
    sizes: STANDARD_SIZES,
    colors: ["Black", "Red", "White"],
    features: [
      "Set of 100 characters",
      "Injection-molded polycarbonate",
      "UV-resistant eco-friendly inks",
      "Racks into Pronto Track instantly",
      "No tools required for changes",
    ],
  },
  // Accessories
  {
    id: "pronto-track",
    name: "Pronto Track",
    slug: "pronto-track",
    category: "accessory",
    description: "Extruded aluminum track system for mounting Pronto™ changeable letters. Available in Top, Dual, and Bottom configurations in 6′, 8′, and 10′ lengths. Installs with rivets or adhesive — no special tools required.",
    price: 0,
    image: productProntoTrack,
    features: [
      "Extruded aluminum construction",
      "Top, Dual, and Bottom track configurations",
      "Available in 6′, 8′, and 10′ lengths",
      "Install with rivets or WELD-ON adhesive",
      "Dual track automatically spaces rows",
      "Compatible with all Pronto letter sizes (3″–10″)",
      "Expands/contracts with temperature changes",
    ],
    variants: [
      { label: "6' Top", price: 3405 },
      { label: "8' Top", price: 4521 },
      { label: "10' Top", price: 5637 },
      { label: "6' Dual", price: 3573 },
      { label: "8' Dual", price: 4764 },
      { label: "10' Dual", price: 5949 },
      { label: "6' Bottom", price: 2778 },
      { label: "8' Bottom", price: 3720 },
      { label: "10' Bottom", price: 4656 },
    ],
  },
  {
    id: "track-rivets",
    name: "Track Rivets",
    slug: "track-rivets",
    category: "accessory",
    description: "Pop rivets for securing Pronto Track to sign panels. Pack of 100.",
    price: 0,
    image: productTrackRivets,
    features: [
      "Pack of 100 rivets",
      "Compatible with all Pronto Track",
      "Easy installation with rivet tool",
    ],
    variants: [
      { label: "Large (100 count)", price: 4422 },
      { label: "Medium (100 count)", price: 4846 },
    ],
  },
  {
    id: "change-arm",
    name: "Change Arm Pole",
    slug: "change-arm",
    category: "accessory",
    description: "Telescoping aluminum pole for changing letters on elevated signs. Extends for safe, ground-level letter changes.",
    price: 0,
    image: productChangeArm,
    features: [
      "Telescoping aluminum construction",
      "Lightweight and durable",
      "Change letters from ground level",
      "Works with all Pronto letter sizes",
    ],
    variants: [
      { label: "6'–12' with Head Assembly", price: 16757 },
      { label: "6'–12' Pole Only", price: 15264 },
      { label: "6'–18' with Head Assembly", price: 21734 },
      { label: "6'–18' Pole Only", price: 20241 },
      { label: "8'–24' with Head Assembly", price: 25549 },
      { label: "8'–24' Pole Only", price: 23392 },
    ],
  },
  {
    id: "storage-cabinet",
    name: "Letter Storage Cabinet",
    slug: "storage-cabinet",
    category: "accessory",
    description: "Organized storage for your Pronto changeable letter inventory. Keep letters sorted and protected.",
    price: 0,
    image: productStorageCase2,
    features: [
      "Organized letter storage",
      "Keeps inventory sorted",
      "Durable construction",
      "Available with or without wheels",
    ],
    variants: [
      { label: "4\"–6\" Plain", price: 15700 },
      { label: "4\"–6\" With Wheels", price: 16171 },
      { label: "8\"–10\" Plain", price: 21706 },
      { label: "8\"–10\" With Wheels", price: 22358 },
    ],
    variantImages: {
      "Plain": productStorageCase2,
      "With Wheels": productStorageCabinet,
    },
  },
];

export function getProductBySlug(slug: string): Product | undefined {
  return products.find((p) => p.slug === slug);
}

export function getProductsByCategory(category: ProductCategory): Product[] {
  return products.filter((p) => p.category === category);
}

export function getLetterSetsByStyle(style: LetterStyle): Product[] {
  return products.filter((p) => p.category === "letter-set" && p.style === style);
}
